import React from "react";
import { MessageSquare, Search } from "lucide-react";

const MINT = "#10b981";
const PURPLE = "#a855f7";

const Avatar = ({ src, name, online }) => {
  const initial = (name || "?").charAt(0).toUpperCase();

  return (
    <div className="relative shrink-0">
      <div
        className="h-12 w-12 overflow-hidden rounded-full"
        style={{
          background: `conic-gradient(from 140deg, ${PURPLE}, ${MINT}, ${PURPLE})`,
          padding: 1.5,
        }}
      >
        <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-[#0b0a14] text-sm font-semibold text-white/80">
          {src ? (
            <img src={src} alt={name} className="h-full w-full object-cover" />
          ) : (
            initial
          )}
        </div>
      </div>
      {online && (
        <span
          className="absolute bottom-0 right-0 h-3 w-3 rounded-full ring-2 ring-[#07060d]"
          style={{ background: MINT, boxShadow: `0 0 8px ${MINT}` }}
        />
      )}
    </div>
  );
};

const ConversationRow = ({ conversation, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`group flex w-full items-center gap-3 rounded-2xl border px-3 py-3 text-left transition active:scale-[0.99] ${
      active
        ? "border-emerald-400/30 bg-white/[0.06]"
        : "border-transparent hover:bg-white/5"
    }`}
    style={
      active
        ? {
            boxShadow: `0 8px 30px -16px ${MINT}, inset 0 0 0 1px rgba(16,185,129,0.15)`,
          }
        : undefined
    }
  >
    <Avatar
      src={conversation.avatar}
      name={conversation.username}
      online={conversation.online}
    />
    <div className="min-w-0 flex-1">
      <div className="flex items-center justify-between gap-2">
        <p className="truncate text-sm font-semibold text-white">
          @{conversation.username}
        </p>
        {conversation.timestamp && (
          <span className="shrink-0 text-[10px] uppercase tracking-wider text-white/40">
            {conversation.timestamp}
          </span>
        )}
      </div>
      <div className="mt-0.5 flex items-center justify-between gap-2">
        <p className="truncate text-xs text-white/50">
          {conversation.lastMessage || "Start a conversation"}
        </p>
        {typeof conversation.unreadCount === "number" &&
          conversation.unreadCount > 0 && (
            <span
              className="grid h-5 min-w-[20px] place-items-center rounded-full px-1.5 text-[10px] font-bold text-black"
              style={{ background: MINT, boxShadow: `0 0 12px ${MINT}88` }}
            >
              {conversation.unreadCount}
            </span>
          )}
      </div>
    </div>
  </button>
);

const SkeletonRow = () => (
  <div className="flex items-center gap-3 rounded-2xl px-3 py-3">
    <div className="h-12 w-12 shrink-0 animate-pulse rounded-full bg-white/[0.06]" />
    <div className="flex-1 space-y-2">
      <div className="h-3 w-1/2 animate-pulse rounded bg-white/[0.06]" />
      <div className="h-2.5 w-3/4 animate-pulse rounded bg-white/[0.04]" />
    </div>
  </div>
);

const ChatSidebar = ({
  conversations = [],
  selectedId,
  onSelect,
  loading = false,
  query = "",
  onQueryChange,
}) => {
  const filteredConversations = query
    ? conversations.filter((conversation) =>
        (conversation.username || "")
          .toLowerCase()
          .includes(query.toLowerCase())
      )
    : conversations;

  return (
    <aside className="flex h-full w-full flex-col bg-[#07060d]">
      <div className="border-b border-white/10 px-4 pb-3 pt-4 sm:px-5">
        <div className="mb-3 flex items-center justify-between">
          <h1 className="text-lg font-bold tracking-tight text-white">
            Messages
          </h1>
          <span
            className="grid h-8 w-8 place-items-center rounded-xl border border-white/10 bg-white/5 text-white/70"
            style={{ boxShadow: "inset 0 0 0 1px rgba(168,85,247,0.15)" }}
          >
            <MessageSquare size={15} />
          </span>
        </div>

        <label className="relative block">
          <Search
            size={15}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
          />
          <input
            type="text"
            value={query}
            onChange={(event) => onQueryChange?.(event.target.value)}
            placeholder="Search conversations"
            className="w-full rounded-2xl border border-white/10 bg-white/[0.04] py-2.5 pl-9 pr-3 text-sm text-white placeholder:text-white/40 outline-none transition focus:border-emerald-400/40 focus:bg-white/[0.06] focus:ring-2 focus:ring-emerald-500/20"
          />
        </label>
      </div>

      <div className="flex-1 overflow-y-auto px-2 py-2 [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.18)_transparent] sm:px-3">
        {loading ? (
          <div className="space-y-1">
            {Array.from({ length: 6 }).map((_, index) => (
              <SkeletonRow key={index} />
            ))}
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <p className="text-sm font-medium text-white/70">
              No conversations yet
            </p>
            <p className="mt-1 text-xs text-white/40">
              Your messages with creators and editors will appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {filteredConversations.map((conversation) => (
              <ConversationRow
                key={conversation.id}
                conversation={conversation}
                active={selectedId === conversation.id}
                onClick={() => onSelect?.(conversation.id)}
              />
            ))}
          </div>
        )}
      </div>
    </aside>
  );
};

export default ChatSidebar;
